import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  Sparkles, 
  SlidersHorizontal, 
  X, 
  ArrowRight,
  Loader2
} from 'lucide-react';
import clsx from 'clsx';
import type { SearchMode, FilterState } from '../types';
import { RANKS, VESSEL_CATEGORIES } from '../types';

interface SearchBoxProps {
  searchMode: SearchMode;
  onSearchModeChange: (mode: SearchMode) => void;
  onSearch: (query: string) => void;
  onFilterSearch: () => void;
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  isLoading: boolean;
  initialQuery?: string;
}

const SUGGESTIONS = [
  "Find third engineers available for very large vessels",
  "Chief officers with 24+ months experience",
  "Show masters available within 30 days",
  "Fourth engineers for medium vessels with 2+ contracts",
];

export function SearchBox({
  searchMode,
  onSearchModeChange,
  onSearch,
  onFilterSearch,
  filters,
  onFiltersChange,
  isLoading,
  initialQuery = '',
}: SearchBoxProps) {
  const [query, setQuery] = useState(initialQuery);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setQuery(initialQuery);
  }, [initialQuery]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchMode === 'ai') {
      onSearch(query);
    } else {
      onFilterSearch();
    }
    setShowSuggestions(false);
  };

  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
    onSearch(suggestion);
    setShowSuggestions(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.2 }}
      className="card"
    >
      {/* Mode Toggle */}
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => onSearchModeChange('ai')}
          className={clsx(
            'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200',
            searchMode === 'ai'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          )}
        >
          <Sparkles className="w-4 h-4" />
          AI Search
        </button>
        <button
          onClick={() => onSearchModeChange('filters')}
          className={clsx(
            'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200',
            searchMode === 'filters'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          )}
        >
          <SlidersHorizontal className="w-4 h-4" />
          Filters
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <AnimatePresence mode="wait">
          {searchMode === 'ai' ? (
            <motion.div
              key="ai-search"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="space-y-3"
            >
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <Search className="w-5 h-5 text-gray-400" />
                </div>
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  placeholder="Try: 'Find third engineers available for very large vessels'"
                  className="input-field pl-10 pr-28"
                />
                <div className="absolute inset-y-0 right-2 flex items-center gap-2">
                  {query && (
                    <button
                      type="button"
                      onClick={() => setQuery('')}
                      className="p-1 rounded hover:bg-gray-100 transition-colors"
                    >
                      <X className="w-4 h-4 text-gray-400" />
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={isLoading || !query.trim()}
                    className="btn-primary py-1.5 px-3 flex items-center gap-1.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        Search
                        <ArrowRight className="w-3.5 h-3.5" />
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Suggestions */}
              <AnimatePresence>
                {showSuggestions && !query && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="space-y-2"
                  >
                    <p className="text-sm text-gray-500">Try these searches:</p>
                    <div className="flex flex-wrap gap-2">
                      {SUGGESTIONS.map((suggestion, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handleSuggestionClick(suggestion)}
                          className="px-3 py-1.5 rounded-full bg-gray-100 text-sm text-gray-600 
                                   hover:bg-gray-200 hover:text-gray-900 transition-colors duration-200"
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ) : (
            <motion.div
              key="filter-search"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Rank Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Rank
                  </label>
                  <select
                    value={filters.rank}
                    onChange={(e) => onFiltersChange({ ...filters, rank: e.target.value })}
                    className="input-field"
                  >
                    <option value="">All Ranks</option>
                    {RANKS.map((rank) => (
                      <option key={rank.value} value={rank.value}>
                        {rank.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Vessel Category Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Vessel Category
                  </label>
                  <select
                    value={filters.vesselCategory}
                    onChange={(e) => onFiltersChange({ ...filters, vesselCategory: e.target.value })}
                    className="input-field"
                  >
                    <option value="">All Categories</option>
                    {VESSEL_CATEGORIES.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Min Experience Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Min Experience (months)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={filters.minExperience}
                    onChange={(e) => onFiltersChange({ ...filters, minExperience: parseInt(e.target.value) || 0 })}
                    className="input-field"
                    placeholder="0"
                  />
                </div>

                {/* Min Contracts Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Min Contracts
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={filters.minContracts}
                    onChange={(e) => onFiltersChange({ ...filters, minContracts: parseInt(e.target.value) || 0 })}
                    className="input-field"
                    placeholder="0"
                  />
                </div>

                {/* Availability Days Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Available Within (days)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={filters.availabilityDays}
                    onChange={(e) => onFiltersChange({ ...filters, availabilityDays: parseInt(e.target.value) || 0 })}
                    className="input-field"
                    placeholder="30"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn-primary flex items-center gap-2"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Search className="w-4 h-4" />
                      Search with Filters
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </form>
    </motion.div>
  );
}
